"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import {
  Sparkles,
  Sun,
  Moon,
  ArrowRight,
  ShieldCheck,
  Cpu,
  Workflow,
  CheckCircle2,
  Circle,
  Globe,
} from "lucide-react";
import { useTheme } from "./src/ThemeContext.jsx";

/**
 * Home — Sikandar Tareen (Complete Portfolio v2)
 * Mirrors Hire Me page UI/UX + background
 * Sections: Hero, Capabilities, AI Agents, ML Models & Stack, Featured Projects, Outcomes, FAQ, CTA, Footer
 */
export default function HomePage() {
  const { darkMode, toggleTheme } = useTheme();

  return (
    <div className={darkMode ? "min-h-screen bg-[#0a0c10] text-white" : "min-h-screen bg-white text-gray-900"}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] px-3 py-2 rounded-md bg-indigo-600 text-white">Skip to content</a>

      {/* NAV — mirrors Hire Me */}
      <header className="sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto mt-4 px-4 sm:px-6">
          <div className={`backdrop-blur rounded-2xl shadow-sm flex items-center justify-between px-4 sm:px-6 py-3 ${darkMode ? 'bg-white/5 border border-white/10' : 'bg-white/80 border border-white/90'}`}>
            <a href="/" className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-white ${darkMode ? 'bg-gradient-to-br from-indigo-500 to-cyan-400' : 'bg-gradient-to-br from-indigo-600 to-cyan-500 drop-shadow'}`}>ST</div>
              <div className="leading-tight">
                <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sikandar Tareen</div>
                <div className={`text-[11px] ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Agentic AI Engineer</div>
              </div>
            </a>
            <div className={`hidden md:flex items-center gap-6 text-sm ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
              <a href="/" className={`${darkMode ? 'text-white' : 'text-black'} font-medium`}>Home</a>
              <a href="/#agents" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Agents</a>
              <a href="/#models" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Models</a>
              <a href="/projects" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Projects</a>
              <a href="/blog" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Blog</a>
              <a href="/about-me" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>About</a>

              <ButtonPrimary darkMode={darkMode} href="/contact" small>
                <Sparkles size={14} /> Contact Me
              </ButtonPrimary>
            </div>
          </div>
        </nav>
      </header>

      {/* Background — same as Hire Me */}
      <ParallaxField darkMode={darkMode} />

      <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-0 [perspective:1000px]">
        {/* Hero */}
        <section className="relative overflow-visible min-h-screen flex items-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="relative z-10 flex flex-col items-center text-center lg:items-start lg:text-left">
              <div className="flex flex-wrap gap-2">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${darkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}>
                  <Circle size={14} className="fill-green-500 text-green-500"/> Available for work
                </div>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${darkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}>
                  <Globe size={14} className="text-blue-500"/> Remote only
                </div>
              </div>
              <h1 className="mt-3 text-5xl sm:text-6xl font-normal tracking-tight">
                <span className="font-bold">Sikandar Tareen</span><br/>
                <span className="text-3xl sm:text-4xl">Agentic AI Engineer</span>
              </h1>
              <p className={`mt-4 text-lg max-w-2xl ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>
                Transforming Ideas into Autonomous, AI-Driven Solutions for a Smarter Tomorrow, Empowering Businesses to Innovate, Optimize, and Scale with Cutting-Edge Technology.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Magnetic>
                  <ButtonPrimary darkMode={darkMode} href="/contact">Contact Me <ArrowRight size={16}/></ButtonPrimary>
                </Magnetic>
                <TiltCard darkMode={darkMode} className={`${darkMode ? 'bg-white/10 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'} rounded-xl`}>
                  <a href="/projects" className="block px-5 py-3">View projects</a>
                </TiltCard>
              </div>
            </motion.div>
            
            {/* Hero Image */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }} 
              className="relative flex justify-center items-center"
            >
              <div className="relative group perspective-1000">
                <div className="relative transform-gpu transition-all duration-500 hover:rotate-y-12 hover:rotate-x-6 hover:scale-105">
                  <img 
                    src="/SikandarTareen2.jpg" 
                    alt="Sikandar Tareen - Agentic AI Engineer"
                    className="w-[36rem] h-[44rem] object-cover rounded-2xl shadow-2xl border-4 border-white/20 backdrop-blur-sm bg-white"
                    style={{
                      filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.3))',
                      transform: 'rotateY(0deg) rotateX(0deg)',
                      transformStyle: 'preserve-3d'
                    }}
                  />
                  {/* 3D Glow Effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl transform translate-z-[-10px]"></div>
                  {/* Floating particles */}
                  <div className="absolute -inset-4 opacity-30">
                    <div className="absolute top-4 left-4 w-2 h-2 bg-blue-400 rounded-full animate-float"></div>
                    <div className="absolute top-8 right-6 w-1 h-1 bg-purple-400 rounded-full animate-float" style={{animationDelay: '1s'}}></div>
                    <div className="absolute bottom-6 left-8 w-1.5 h-1.5 bg-pink-400 rounded-full animate-float" style={{animationDelay: '2s'}}></div>
                    <div className="absolute bottom-4 right-4 w-1 h-1 bg-green-400 rounded-full animate-float" style={{animationDelay: '0.5s'}}></div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>



        {/* AI Agents */}
        <section id="agents" className="mt-16">
          <h2 className="text-2xl font-bold">AI Agents</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <AgentCard darkMode={darkMode}
              icon={<Workflow/>}
              title="Orchestrated tool‑using agents"
              bullets={["Function/tool calling","Memory & context windows","Budgets & timeouts"]}
              desc="Action via tools/APIs with retries, fallbacks, and typed schemas. Safe by default with approvals and rate limits."/>
            <AgentCard darkMode={darkMode}
              icon={<Cpu/>}
              title="RAG & planning agents"
              bullets={["Query rewriting & decomposition","Rerankers & hybrid search","Hallucination controls"]}
              desc="Retrieval with freshness policies, reranking, and structured planning for multi‑step tasks."/>
            <AgentCard darkMode={darkMode}
              icon={<ShieldCheck/>}
              title="Governed multi‑agent pipelines"
              bullets={["Red teaming & scenario tests","Quality gates & rollbacks","Observability & tracing"]}
              desc="Separation of duties, approvals, audit trails, and evaluation harnesses for production safety."/>
          </div>
        </section>

        {/* ML Models & Preferred Stack */}
        <section id="models" className="mt-24">
          <h2 className="text-2xl font-bold">ML Models & Tech Stack</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mlModels.map(m => (
              <TiltCard key={m.name} darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'} rounded-2xl p-5 border`}>
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold">{m.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-white/10 text-white/80' : 'bg-gray-100 text-gray-700'}`}>{m.type}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs opacity-80">
                  <span className={`${darkMode ? 'bg-white/10 border-white/10' : 'bg-gray-50 border-gray-200'} border px-2 py-1 rounded-full`}>{m.provider}</span>
                  {m.tags?.map(t => (
                    <span key={m.name+t} className={`${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border px-2 py-1 rounded-full`}>{t}</span>
                  ))}
                </div>
                <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'} text-sm mt-3`}>{m.desc}</p>
              </TiltCard>
            ))}
          </div>

          <div className="mt-10">
            <div className="text-sm font-medium opacity-80">Preferred Stack</div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {preferredStack.map(s => (
                <span key={s} className={`${darkMode ? 'bg-white/5 border-white/10 text-white/80' : 'bg-white border-gray-200 text-gray-700'} border px-2.5 py-1.5 rounded-full`}>{s}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="mt-24">
          <div className="flex items-end justify-between">
            <h2 className="text-2xl font-bold">Featured Projects</h2>
            <a href="/projects" className={`${darkMode ? 'text-white/70 hover:text-white' : 'text-gray-700 hover:text-black'} text-sm`}>See all →</a>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {homeProjects.map((p) => <ProjectPreview key={p.t} p={p} darkMode={darkMode} />)}
          </div>
          <div className="mt-8"></div>
        </section>



        {/* CTA */}
        <section className="mt-16">
          <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'from-indigo-600/25 to-cyan-500/25 border-white/10 text-white' : 'from-indigo-100 to-cyan-100 border-gray-200 text-gray-900'} rounded-3xl p-8 bg-gradient-to-r border`}>
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 justify-between">
              <div>
                <h3 className="text-xl font-bold">Let’s scope your agentic MVP</h3>
                <p className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-sm mt-1`}>Discovery → prototype → hardening. Clear milestones, measurable results.</p>
              </div>
              <Magnetic>
                <ButtonPrimary darkMode={darkMode} href="/contact">Request proposal <ArrowRight size={16}/></ButtonPrimary>
              </Magnetic>
            </div>
          </TiltCard>
        </section>
      </main>

      {/* Footer */}
      <footer className={`py-10 text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'} border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">© {new Date().getFullYear()} Sikandar Tareen</div>
      </footer>

      {/* Theme toggle */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border transition focus:outline-none focus:ring-2 ${darkMode ? 'border-white/20 bg-[#1a1c20] text-white hover:opacity-90 focus:ring-cyan-300/60' : 'border-gray-300 bg-white/95 backdrop-blur text-gray-800 hover:opacity-90 focus:ring-indigo-500'}`}
          aria-pressed={darkMode}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          {darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>
    </div>
  );
}

/* -------------------- Data -------------------- */
const mlModels = [
  { name: 'GPT‑4o', type: 'LLM', provider: 'OpenAI', tags: ['tools','json','reasoning'], desc: 'Advanced reasoning, coding, tool‑use and multi‑modal capabilities for agent workflows.' },
  { name: 'Claude 3.5 Sonnet', type: 'LLM', provider: 'Anthropic', tags: ['long context','safety'], desc: 'Long‑context understanding with careful, interpretable outputs and strong instruction following.' },
  { name: 'Llama 3.1 70B', type: 'LLM', provider: 'Meta', tags: ['open source','self-host'], desc: 'Open‑source powerhouse for self‑hosted inference and customization.' },
  { name: 'Mistral Large', type: 'LLM', provider: 'Mistral', tags: ['efficient','multilingual'], desc: 'Efficient, multilingual model suitable for cost‑sensitive workloads.' },
  { name: 'text-embedding-3-large', type: 'Embedding', provider: 'OpenAI', tags: ['RAG','search'], desc: 'High‑quality embeddings for semantic search and retrieval‑augmented generation.' },
  { name: 'bge-large', type: 'Embedding', provider: 'BAAI', tags: ['open source','RAG'], desc: 'Open‑source embedding model optimized for retrieval tasks.' },
  { name: 'E5-large-v2', type: 'Embedding', provider: 'Microsoft', tags: ['RAG','classification'], desc: 'Robust general‑purpose embeddings for RAG and zero‑shot tasks.' },
  { name: 'Cohere Rerank v3', type: 'Reranker', provider: 'Cohere', tags: ['rerank','precision'], desc: 'Improves retrieval precision when paired with vector search.' },
  { name: 'Jina Reranker v2', type: 'Reranker', provider: 'Jina AI', tags: ['rerank','fast'], desc: 'Fast, accurate reranking suitable for real‑time search flows.' },
  { name: 'GPT‑4o mini (vision)', type: 'Vision', provider: 'OpenAI', tags: ['vision','OCR'], desc: 'Image understanding, OCR, charts/tables parsing for agents.' },
  { name: 'Claude 3.5 Sonnet (vision)', type: 'Vision', provider: 'Anthropic', tags: ['vision','analysis'], desc: 'Visual reasoning useful for document QA and dashboards.' },
  { name: 'Whisper large‑v3', type: 'Speech‑to‑Text', provider: 'OpenAI', tags: ['ASR','multilingual'], desc: 'Accurate transcription for multi‑language audio inputs.' },
];

const preferredStack = ['pgvector','Milvus','Pinecone','LangChain','LlamaIndex','OpenAI Assistants','Ragas','DeepEval','LangSmith','vLLM','TGI','Langfuse','OpenTelemetry'];

const homeProjects = [
  { t: 'Autonomous Incident Responder', s: 'Security • SOC', bullets: ['Automated triage', 'Approvals & audit', 'Evidence capture'] },
  { t: 'Research Copilot', s: 'Knowledge • RAG', bullets: ['Citation‑first', 'Verification loop', 'Freshness policy'] },
  { t: 'Conversational Cloud Ops', s: 'DevOps • IaC', bullets: ['Safe tool use', 'Rollback', 'Policy checks'] },
];



/* -------------------- Components -------------------- */
function ButtonPrimary({ children, href = '#', darkMode, small = false }) {
  return (
    <a href={href} className={`inline-flex items-center gap-2 ${small ? 'px-3 py-1.5 text-sm' : 'px-5 py-3'} rounded-xl font-medium bg-blue-600 text-white shadow hover:opacity-95 transition focus:outline-none focus-visible:ring-2 ${darkMode ? 'focus-visible:ring-cyan-300/60' : 'focus-visible:ring-indigo-500'}`}>
      {children}
    </a>
  );
}

function ProjectPreview({ p, darkMode }) {
  return (
    <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/20' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-100'} rounded-2xl p-5 border`}>
      <div className={`text-xs ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>{p.s}</div>
      <h3 className="mt-1 font-semibold">{p.t}</h3>
      <ul className={`${darkMode ? 'text-white/80' : 'text-gray-700'} mt-2 text-sm space-y-1`}>{p.bullets.map((b)=> <li key={b}>• {b}</li>)}</ul>
      <div className="mt-4">
        <a href="/projects" className={`${darkMode ? 'text-white/80 hover:text-white' : 'text-gray-700 hover:text-black'} inline-flex items-center gap-1 text-sm`}>See case study <ArrowRight size={14}/></a>
      </div>
    </TiltCard>
  );
}



function AgentCard({ icon, title, desc, bullets, darkMode }) {
  return (
    <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'} rounded-2xl p-6 border`}>
      <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg w-fit`}>{icon}</div>
      <div className="mt-3 font-semibold">{title}</div>
      <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'} text-sm mt-1`}>{desc}</p>
      <ul className={`${darkMode ? 'text-white/80' : 'text-gray-700'} mt-3 text-sm space-y-1`}>
        {bullets.map((b) => <li key={b}>• {b}</li>)}
      </ul>
    </TiltCard>
  );
}

// Subtle 3D tilt + magnetic helpers
function TiltCard({ children, className = '', glow = false, darkMode = false }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const tx = useSpring(rx, { stiffness: 140, damping: 18 });
  const ty = useSpring(ry, { stiffness: 140, damping: 18 });
  function onMove(e) {
    const el = ref.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; const py = (e.clientY - rect.top) / rect.height;
    const ROT = 4; rx.set((0.5 - py) * ROT); ry.set((px - 0.5) * ROT);
    el.style.setProperty('--mx', `${Math.round(px * 100)}%`);
    el.style.setProperty('--my', `${Math.round(py * 100)}%`);
  }
  function onLeave(){ rx.set(0); ry.set(0); }
  const glowColor = darkMode ? 'rgba(34,211,238,0.18)' : 'rgba(99,102,241,0.18)';
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ rotateX: tx, rotateY: ty, transformStyle: 'preserve-3d' }} className={`group relative will-change-transform transition-shadow hover:-translate-y-0.5 ${className}`}>
      {glow && (
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] blur-2xl opacity-0 group-hover:opacity-30 transition-opacity" style={{ background: `radial-gradient(520px circle at var(--mx,50%) var(--my,50%), ${glowColor}, transparent 45%)` }} />
      )}
      {children}
    </motion.div>
  );
}

function Magnetic({ children }) {
  const ref = useRef(null);
  const x = useMotionValue(0); const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 120, damping: 12 });
  const sy = useSpring(y, { stiffness: 120, damping: 12 });
  function onMove(e){ const el = ref.current; if (!el) return; const r = el.getBoundingClientRect(); x.set(((e.clientX - r.left)/r.width - 0.5) * 8); y.set(((e.clientY - r.top)/r.height - 0.5) * 8);} 
  function onLeave(){ x.set(0); y.set(0); }
  return (<motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ x: sx, y: sy }} className="inline-block">{children}</motion.div>);
}

function ParallaxField({ darkMode }) {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div
        className={`absolute left-1/4 top-10 w-40 h-40 rounded-full ${
          darkMode ? "bg-white/5" : "bg-indigo-100"
        } blur-3xl animate-[float_10s_ease-in-out_infinite]`}
      />
      <div
        className={`absolute right-10 top-1/3 w-24 h-24 rounded-full ${
          darkMode ? "bg-indigo-600/20" : "bg-indigo-200/70"
        } blur-2xl animate-[float_12s_ease-in-out_infinite]`}
      />
      <div
        className={`absolute left-10 bottom-10 w-24 h-24 rounded-full ${
          darkMode ? "bg-cyan-500/20" : "bg-cyan-200/70"
        } blur-2xl animate-[float_14s_ease-in-out_infinite]`}
      />
      <style>{`@keyframes float { 0%,100%{ transform: translateY(0)} 50%{ transform: translateY(-12px)} }`}</style>
    </div>
  );
}
